/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { baixarExtrato, baixarPdfLancamento } from '../../../../../../hooks/useContaCilExtratos';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';

interface Props {
  extratoId?: number | null;
  lancamentoId?: number | null;
  nomeArquivo?: string | null;
  formato?: string | null; // PDF | XLSX | XLS | CSV | OFX
  isOpen: boolean;
  onClose: () => void;
}

// Formatos que renderizam direto no iframe (browser sabe abrir).
// XLSX/XLS exigem app externo — só botão de download + abrir externamente.
const FORMATOS_INLINE = new Set(['PDF', 'CSV', 'OFX', 'TXT']);

function rotuloFormato(formato?: string | null): string {
  const f = (formato || '').toUpperCase();
  if (!f) return 'arquivo';
  if (f === 'PDF') return 'PDF';
  if (f === 'OFX') return 'OFX';
  if (f === 'CSV') return 'CSV';
  if (f === 'XLSX' || f === 'XLS') return 'planilha';
  return f;
}

/**
 * Visualiza o arquivo do extrato inline quando o formato permite (PDF, CSV,
 * OFX renderizam direto no iframe do browser). XLSX/XLS mostram um aviso com
 * botão pra baixar/abrir em nova aba. Padrão herdado do viewPdfModal antigo.
 */
export function ExtratoPdfModal({
  extratoId,
  lancamentoId,
  nomeArquivo,
  formato,
  isOpen,
  onClose,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [nomeFetched, setNomeFetched] = useState<string | null>(null);

  const formatoUpper = (formato || '').toUpperCase();
  const rotulo = rotuloFormato(formato);
  const podeRenderizar = !formato || FORMATOS_INLINE.has(formatoUpper);

  useEffect(() => {
    if (!isOpen || (extratoId == null && lancamentoId == null)) return undefined;
    let cancelado = false;
    setLoading(true);
    setErro(null);
    setUrl(null);

    (async () => {
      try {
        const r =
          lancamentoId != null
            ? await baixarPdfLancamento(lancamentoId)
            : await baixarExtrato(extratoId as number, true);
        if (cancelado) return;
        setNomeFetched(r.nome_arquivo ?? null);
        // Usa a URL presigned inline DIRETO no iframe. O backend ja forca o
        // Content-Type + Content-Disposition=inline na URL, entao renderiza
        // sem fetch+blob (que o CORS do S3 bloqueava -> "so baixava").
        setUrl(r.url);
      } catch (e) {
        if (!cancelado) setErro(extractApiErrorMessage(e, 'Não foi possível abrir o arquivo.'));
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [isOpen, extratoId, lancamentoId, formato]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '5xl' }} isCentered>
      <ModalOverlay
        bg="rgba(15, 23, 42, 0.5)"
        backdropFilter="blur(8px)"
        sx={{ WebkitBackdropFilter: 'blur(8px)' }}
      />
      <ModalContent
        bg="rgba(255,255,255,0.9)"
        backdropFilter="blur(40px) saturate(200%)"
        sx={{ WebkitBackdropFilter: 'blur(40px) saturate(200%)' }}
        borderRadius={{ base: 'none', md: '2xl' }}
        maxH={{ base: '100dvh', md: '92vh' }}
      >
        <ModalHeader fontSize="md" color="#0F172A" pr={12}>
          <Text isTruncated>
            {nomeArquivo || nomeFetched || `Extrato (${rotulo.toUpperCase()})`}
          </Text>
        </ModalHeader>
        <ModalCloseButton color="#64748B" />
        <ModalBody pb={5}>
          {loading && (
            <Flex direction="column" align="center" justify="center" h="70vh" gap={3}>
              <Spinner size="lg" color="#1B88F5" thickness="3px" />
              <Text fontSize="sm" color="#64748B">
                {`Carregando ${rotulo}…`}
              </Text>
            </Flex>
          )}
          {erro && (
            <Flex direction="column" align="center" justify="center" h="70vh" gap={3}>
              <Text fontSize="sm" color="#DC2626" textAlign="center">
                {erro}
              </Text>
              {url && (
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  onClick={() => window.open(url, '_blank')}
                >
                  Abrir em nova aba
                </Button>
              )}
            </Flex>
          )}
          {/* Formato sem render inline (XLSX/XLS): avisa + botão pra abrir */}
          {url && !loading && !erro && !podeRenderizar && (
            <Flex direction="column" align="center" justify="center" h="70vh" gap={3}>
              <Text fontSize="sm" color="#475569" textAlign="center">
                Pré-visualização não disponível para {formatoUpper}.
              </Text>
              <Text fontSize="xs" color="#94A3B8" textAlign="center">
                Use o botão abaixo para abrir/baixar o arquivo.
              </Text>
              {url && (
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  onClick={() => window.open(url, '_blank')}
                >
                  Abrir em nova aba
                </Button>
              )}
            </Flex>
          )}
          {url && !loading && !erro && podeRenderizar && (
            <Box
              borderRadius="lg"
              overflow="hidden"
              borderWidth="1px"
              borderColor="rgba(15,23,42,0.08)"
            >
              <iframe src={url} style={{ width: '100%', height: '74vh', border: 'none' }} />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
